import React from 'react';
import { useParams, useHistory, Switch, Route } from 'react-router-dom';
import { Tabs as MuiTabs, Tab as MuiTab } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { setActivePageTab, clearActivePageTab } from './active-tab-store';
import { selectedTabCorners } from './selectedTabCorners';

export const TabSubrouter = ({
  children = undefined,
  baseUrl,
  subroutes,
  urlParam = 'tab',
  ...props
}: any) => {
  const params = useParams();
  const history = useHistory();
  const tab = params[urlParam];

  const transformBaseUrl = React.useCallback(
    (path: any) => {
      const pathArray = path.split('/');
      return pathArray
        .map((node: any) => {
          if (node.startsWith(':')) {
            return params[node.slice(1)];
          }
          return node;
        })
        .join('/');
    },
    [params]
  );

  // Publish the active tab so the Ask-Moo screen context can tell the chat agent
  // which tab the user is on (subTab). Mirrors the active-tab selection the
  // `<Tabs>` render below uses: the tab whose `value || path` equals the URL
  // param, falling back to the pathless index tab when the param is absent.
  const resolvedBaseUrl = React.useMemo(
    () => transformBaseUrl(baseUrl),
    [transformBaseUrl, baseUrl]
  );
  const activeTabLabel = React.useMemo(() => {
    const active = subroutes.find(
      (route: any) => (route.value || route.path || null) === (tab || null)
    );
    // Labels are usually strings but may be React nodes; only a string is useful
    // as a subTab value, so skip node labels rather than stringifying them.
    return typeof active?.label === 'string' ? active.label : undefined;
  }, [subroutes, tab]);
  React.useEffect(() => {
    setActivePageTab(activeTabLabel, resolvedBaseUrl);
    return () => clearActivePageTab(resolvedBaseUrl);
  }, [activeTabLabel, resolvedBaseUrl]);

  const handleChange = (e: any, nextTab: any) => {
    if (typeof props.onChange === 'function') {
      return props.onChange(nextTab);
    }
    if (nextTab) {
      history.push(`${transformBaseUrl(baseUrl)}/${nextTab}`);
    } else {
      history.push(`${transformBaseUrl(baseUrl)}`);
    }
  };

  return (
    <>
      {typeof children === 'function' ? (
        <>
          {children({ onChange: handleChange, value: tab || null, subroutes })}
        </>
      ) : (
        <Tabs
          value={tab || null}
          onChange={handleChange}
          subroutes={subroutes}
        />
      )}
      <Switch>
        {subroutes.map((route: any) => {
          const { path, label, value, ...routeRest } = route;
          return (
            <Route
              exact
              key={`tab-subroutes-${path}`}
              path={tab ? `${baseUrl}/${path}` : `${baseUrl}`}
              {...routeRest}
            />
          );
        })}
      </Switch>
    </>
  );
};

export const Tabs = ({ subroutes, tabProps, ...props }: any) => {
  return (
    <TabsContainer {...props}>
      {subroutes.map((route: any) =>
        route.label ? (
          <Tab
            key={`tab-${route.path}`}
            value={route.value || route.path || null}
            label={route.label}
            data-qa={`TAB_NAVIGATION:${
              typeof route.label === 'string' ? route.label : route.path
            }`}
            {...route.tabProps}
            {...tabProps}
          />
        ) : null
      )}
    </TabsContainer>
  );
};

export const TabsContainer = ({ ...props }) => {
  const classes = useTabContainerStyles();
  return <MuiTabs classes={classes} variant="scrollable" {...props} />;
};

const useTabContainerStyles = makeStyles(({ palette }) => ({
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  root: {
    overflow: 'visible',
  },
  flexContainer: {
    '& > button:last-of-type': {
      borderRight: `0.5px solid ${palette.border.grey}`,
    },
  },
}));

export const Tab = ({ ...props }) => {
  const classes = useTabStyles(props);
  return <MuiTab disableRipple classes={classes} {...props} />;
};

const useTabStyles = makeStyles(({ config, palette }) => ({
  root: {
    textTransform: 'none',
    color: `${config.colors.cowbellBlue} !important`,
    '&:focus': {
      opacity: 1,
    },
    // A ~10-char minimum (not a fixed width) + nowrap: short labels get a
    // consistent minimum so tabs don't cram together, and long ones (e.g.
    // "Policy Transactions") grow to a single line instead of wrapping.
    // `!important` because MUI's scrollable-variant sets the Tab min-width to
    // `auto`, which otherwise overrides this.
    minWidth: '10ch !important',
    whiteSpace: 'nowrap',
    // Generous horizontal padding so a text-sized tab (e.g. a single long
    // "Underwriting" tab) has breathing room on both sides instead of the
    // label hugging the tab edges.
    padding: '12px 24px',
    fontWeight: 'normal',
    overflow: 'visible',
    borderLeft: `0.5px solid ${palette.border.grey}`,
    fontSize: config.textSizes.tertia,
  },
  selected: (props) => ({
    ...selectedTabCorners(palette.background.paper),
    backgroundColor: palette.background.paper,
    color: `${palette.primary.contrastText} !important`,
    borderRight: 'none !important',
    borderLeft: 'none !important',
    '&.Mui-selected': {
      color: `${palette.primary.contrastText} !important`,
      fontWeight: config.weights.bold,
    },
    '& + button': {
      borderLeft: 'none !important',
    },
    // @ts-expect-error TS(2339): Property 'selectedStyles' does not exist on type '... Remove this comment to see the full error message
    ...props?.selectedStyles,
  }),
}));
